import { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import SectionHead from "./SectionHead";
import useLang from "../i18n/LanguageContext";
import { SENT_EVENT } from "./Critter";
import "./Contact.css";

// Same EmailJS service/template as the previous site; the template reads
// firstName, lastName, email, phone and message.
const SERVICE_ID = "service_izgl1lg";
const TEMPLATE_ID = "template_78ywhhe";
const PUBLIC_KEY = "XXgWEUnqUJkM3K1lt";

const SENT_RESET_MS = 5000;

const ReturnIcon = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 5v7a3 3 0 0 1-3 3H5" />
    <path d="M9 11l-4 4 4 4" />
  </svg>
);

const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const formRef = useRef(null);
  const resetRef = useRef(null);
  // idle | sending | sent | error
  const [status, setStatus] = useState("idle");

  useEffect(() => () => clearTimeout(resetRef.current), []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (status === "sending") return;

    const data = new FormData(formRef.current);
    setStatus("sending");
    emailjs
      .send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          firstName: data.get("name"),
          lastName: "",
          email: data.get("email"),
          phone: "",
          message: data.get("message"),
        },
        PUBLIC_KEY
      )
      .then(
        () => {
          setStatus("sent");
          window.dispatchEvent(new CustomEvent(SENT_EVENT));
          formRef.current.reset();
          clearTimeout(resetRef.current);
          resetRef.current = setTimeout(() => setStatus("idle"), SENT_RESET_MS);
        },
        () => setStatus("error")
      );
  };

  // Ctrl/Cmd + Enter submits from the message box
  const handleMessageKey = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      formRef.current.requestSubmit();
    }
  };

  const enterLabel = {
    idle: c.enter,
    sending: c.sending,
    sent: c.sent,
    error: c.retry,
  }[status];

  return (
    <section id="contact" className="section">
      <div className="section__inner">
        <SectionHead fkey="F5" title={c.title} intro={c.intro} />
        <form ref={formRef} className="contact" onSubmit={handleSubmit}>
          <div className="contact__fields">
            <label className="contact__label">
              {c.name}
              <input className="contact__input" type="text" name="name" data-critter="name" autoComplete="name" required />
            </label>
            <label className="contact__label">
              {c.email}
              <input className="contact__input" type="email" name="email" data-critter="email" autoComplete="email" required />
            </label>
            <label className="contact__label contact__label--wide">
              {c.message}
              <textarea
                className="contact__input contact__textarea"
                data-critter="message"
                name="message"
                rows="5"
                required
                onKeyDown={handleMessageKey}
              />
            </label>
            <p className="contact__hint">{c.hint}</p>
          </div>

          <div className="contact__send">
            <p className={`contact__status contact__status--${status}`} role="status">
              {status === "sent" && c.success}
              {status === "error" && c.error}
            </p>
            <button
              type="submit"
              className={`key contact__enter is-${status}`}
              data-critter="enter"
              aria-label={c.sendAria}
              disabled={status === "sending"}
            >
              <span className="contact__enter-label">{enterLabel}</span>
              <span className="contact__enter-icon">
                <ReturnIcon />
              </span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
